import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

export const isNative = () => Capacitor.isNativePlatform();

export type PhotoSource = 'camera' | 'library';

const MIME_BY_FORMAT: Record<string, string> = {
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

/**
 * Capture or pick a photo via the native Capacitor Camera plugin and return
 * it as a File suitable for the existing multipart upload endpoint.
 *
 * Only callable on native platforms. On web, callers should keep using the
 * hidden <input type="file"> flow.
 */
export async function pickNativePhoto(source: PhotoSource): Promise<File> {
  if (!isNative()) {
    throw new Error('pickNativePhoto called on non-native platform');
  }

  const photo = await Camera.getPhoto({
    quality: 85,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos,
    saveToGallery: false,
  });

  if (!photo.webPath) {
    throw new Error('Kein Foto erhalten');
  }

  // Convert the native webPath (file://... via capacitor://) into a Blob
  // that fetch can read, then into a File the server expects.
  const res = await fetch(photo.webPath);
  const blob = await res.blob();
  const format = (photo.format || 'jpeg').toLowerCase();
  const mime = MIME_BY_FORMAT[format] ?? 'image/jpeg';
  const ext = format === 'jpg' ? 'jpg' : format;
  return new File([blob], `photo.${ext}`, { type: mime });
}
